import React from 'react'
import cn from 'classnames'
import { observer } from 'mobx-react'

import DropdownMenu from './DropdownMenu'

import Ellipsis from 'assets/icons/ellipsis.svg'
import styles from './dropdown.scss'

export interface IDropdownItem {
  id: string
  icon?: React.ComponentClass
  label: string
}

interface IProps {
  className?: string
  buttonClassName?: string
  type: 'button' | 'text'
  nativeSelect?: boolean
  items: IDropdownItem[]
  selectedItem: IDropdownItem | null
  onSelect: (item: IDropdownItem) => void
  innerRef?: React.RefObject<any>
}

interface IState {
  isOpen: boolean
}

class Dropdown extends React.PureComponent<IProps, IState> {
  private button: React.RefObject<HTMLDivElement> = React.createRef()

  state = {
    isOpen: false,
  }

  handleToggle = () => {
    if (this.state.isOpen) {
      this.handleClose()
    } else {
      this.handleOpen()
    }
  }

  handleSelect = (item: IDropdownItem) => {
    const { onSelect } = this.props
    if (onSelect) {
      onSelect(item)
    }

    // Close in the next tick
    // This is so that any DOMNode.contains() inside click outside handlers can finish up
    setTimeout(() => this.handleToggle(), 0)
  }

  handleOpen = () => {
    setTimeout(
      () =>
        this.setState({
          isOpen: true,
        }),
      0
    )
  }

  handleClose = () => {
    setTimeout(
      () =>
        this.setState({
          isOpen: false,
        }),
      0
    )
  }

  render() {
    const {
      className,
      type,
      nativeSelect,
      items,
      selectedItem,
      onSelect,
      innerRef,
      buttonClassName,
      ...others
    } = this.props
    const { isOpen } = this.state

    return (
      <div className={cn(styles.container, className)}>
        <div
          ref={this.button}
          className={cn(
            styles.button,
            { [styles.open]: isOpen },
            buttonClassName
          )}
          onClick={this.handleToggle}
          {...others}
        >
          {type === 'button' && <Ellipsis />}
          {type === 'text' && (
            <div
              data-cy="dropdown__item--selected"
              className={styles.textButton}
              title={selectedItem?.label}
            >
              <span>{selectedItem?.label || ''}</span>
              <div className={styles.caret} />
            </div>
          )}
        </div>

        {nativeSelect && (
          <select
            ref={innerRef}
            className={styles.select}
            value={selectedItem?.id}
            onChange={
              e =>
                this.handleSelect(
                  items.find(i => i.id === e.currentTarget.value)!
                ) // ! because this item definitely does exist
            }
          >
            {items.map(i => (
              <option key={i.id} value={i.id}>
                {i.label}
              </option>
            ))}
          </select>
        )}

        {isOpen && !nativeSelect && (
          <DropdownMenu
            target={this.button}
            items={items}
            onSelect={this.handleSelect}
            onBlur={this.handleClose}
          />
        )}
      </div>
    )
  }
}

export default observer(Dropdown)
